import { formPages, placementOf } from '@ayumia/component-runtime';
import type { FieldPresetBody } from '@ayumia/contracts';
import { text } from '../../../app/i18n';
import type { InsertResult, LibraryEntry } from '../../editor/canvas-types';
import { getAt, insertAt } from '../../editor/json-pointer';
import { blockSpot, fieldSpot, insertSection, regionTarget } from '../canvas/drop-targets';
import { freshId, SECTION_KINDS } from '../forms-model';
import { withDisplayDefaults } from './block-settings';
import { blocksOf, declaredPages, insertBlock, moveSectionToPage, type Template } from '../layout-model';
import { blockOf, componentByName, currentLayout, flutterSupported, fr, refusalOf, type EditorRegistry } from '../layout-registry';
import { CONTRACT_WIDGET, personInRegion, widgetById, WIDGETS, type Entry, type Section } from './widgets';

/** Widget de l'éditeur pour un `presentation.widget` du contrat (icône d'un préréglage). */
const presetWidgetId = (widget: string | undefined): string => Object.entries(CONTRACT_WIDGET).find(([, contract]) => contract === widget)?.[0] ?? 'text';

/**
 * Bibliothèque de l'éditeur v2 (ADR-146 addendum (14), (16), (18) ; planche « Panneaux-Widgets ») : quatre
 * onglets en segment d'icônes — Widgets (génériques), Parties, Modèle (champs groupés par objet, « déjà sur la
 * page »), Préréglages (en lignes, par couche) — et la zone « À placer » (parties qu'aucune page ne place). Les
 * tuiles portent un mot ; une pastille seulement pour un Pack ou un client (« Gibier », « NEMROD »), jamais
 * « Core ». La recherche porte aussi sur l'origine. Rien n'est rangé hors du gabarit : insérer copie.
 */

export const LIBRARY_GROUPS = ['Widgets', 'Parties', 'Modèle', 'Préréglages'] as const;

/** Icône de chaque onglet (segment à infobulle). */
export const LIBRARY_GROUP_ICONS: Readonly<Record<(typeof LIBRARY_GROUPS)[number], string>> = { Widgets: 'grid', Parties: 'layout', Modèle: 'database', Préréglages: 'copy' };

type Layer = 'core' | 'pack' | 'customer' | 'solution';

/** Champ d'un objet du modèle effectif (`GetEffectiveModel`), tel que la bibliothèque le propose. */
export interface ModelField {
  field_id: string;
  label: Record<string, string>;
  type: string;
  unit?: string;
  reference_type?: string;
  enum_values?: string[];
  origin: Layer;
  lineage?: string;
}

export interface ModelObject {
  object_type: string;
  label: Record<string, string>;
  lineage?: string;
  fields: ModelField[];
}

/** Préréglage de champ actif (`ListFieldPresets`). */
export interface PresetRow {
  artifact_id: string;
  artifact_version: string;
  origin: Layer;
  owner_ref?: string;
  body: FieldPresetBody;
  /** Nombre de formulaires actifs qui le citent. */
  forms: number;
}

/** Données du tenant lues à l'ouverture de l'éditeur ; chacune peut manquer (droit absent, requête à venir). */
export interface StudioCatalog {
  model: readonly ModelObject[];
  presets: readonly PresetRow[];
  /** Nom des couches : `core`, `pack:<id>`, `customer:<id>` → « Pack Gibier », « NEMROD ». */
  layers: Readonly<Record<string, string>>;
  /** Préréglages indisponibles (requête absente ou refusée) : l'onglet le dit. */
  presetsUnavailable?: boolean;
  modelUnavailable?: boolean;
}

export const EMPTY_CATALOG: StudioCatalog = { model: [], presets: [], layers: {} };

/** Nom d'une couche en mots : « Core », « Pack Gibier », « NEMROD » ; jamais un identifiant. */
export function layerLabel(catalog: StudioCatalog, origin: string, ref?: string): string {
  if (origin === 'core') return 'Core';
  if (ref !== undefined) {
    const lineage = ref.startsWith('tenant:') ? undefined : ref;
    const named = lineage === undefined ? undefined : catalog.layers[lineage];
    if (named !== undefined) return named;
  }
  const first = Object.entries(catalog.layers).find(([lineage]) => lineage.startsWith(`${origin === 'customer' ? 'customer' : origin}:`));
  if (first !== undefined && origin === 'customer') return first[1];
  return origin === 'pack' ? 'Pack' : origin === 'customer' ? 'Client' : 'Solution';
}


/** Identifiants des champs déjà dans le gabarit (toutes parties). */
function usedFieldIds(template: Template): Set<string> {
  const used = new Set<string>();
  for (const section of template.sections) {
    for (const entry of section.fields) used.add(entry.field.field_id);
    for (const entry of section.repeat?.children?.fields ?? []) used.add(entry.field.field_id);
  }
  return used;
}

/** Identifiant libre pour un champ : celui proposé s'il est libre, sinon `champ_N`. */
function freeFieldId(template: Template, proposed: string | undefined): string {
  if (proposed !== undefined && /^[a-z][a-z0-9_]*$/.test(proposed) && !usedFieldIds(template).has(proposed)) return proposed;
  return freshId(template, proposed !== undefined && /^[a-z][a-z0-9_]*$/.test(proposed) ? proposed : 'champ');
}

/**
 * Insère une entrée de champ au point donné (même calcul que le clic et le glisser). `stripRegions` : régions du
 * bandeau de la mise en page, où un champ « Personne » se pose en puce (ADR-152 §4).
 */
export function insertField(template: Template, entry: Entry, target: string | null, stripRegions: ReadonlySet<string> = new Set()): InsertResult<Template> {
  const spot = fieldSpot(template, target);
  if (spot === null) return { refused: 'Choisissez d’abord la partie qui recevra le champ.' };
  const section = template.sections[Number(spot.list.split('/')[2])];
  const region = section === undefined ? undefined : placementOf(template, section.section_id).region;
  const placed = personInRegion(entry, region !== undefined && stripRegions.has(region));
  return { document: insertAt(template, spot.list, placed, spot.index), id: `field:${spot.list}/${spot.index}` };
}

/** Régions du bandeau de la mise en page en cours. */
export function stripRegionsOf(registry: EditorRegistry, template: Template): Set<string> {
  return new Set((currentLayout(registry, template)?.body.regions ?? []).filter((region) => (region as { role?: string }).role === 'strip').map((region) => region.region_id));
}

// ---- Widgets.

function widgetEntries(registry: EditorRegistry): LibraryEntry<Template>[] {
  const out: LibraryEntry<Template>[] = [];
  for (const widget of WIDGETS) {
    if (widget.create !== undefined) {
      const create = widget.create;
      out.push({ id: `field:${widget.id}`, label: widget.label, word: widget.word, group: 'Widgets', section: widget.group, icon: widget.icon, meta: widget.meta, layer: 'core', origin: 'Core', insert: (template, target) => insertField(template, create(template), target, stripRegionsOf(registry, template)) });
      continue;
    }
    const component = (widget.blocks ?? []).map((name) => componentByName(registry, name)).find((found) => found !== undefined);
    if (component === undefined) {
      out.push({ id: `field:absent:${widget.id}`, label: widget.label, word: widget.word, group: 'Widgets', section: widget.group, icon: widget.icon, meta: widget.meta, layer: 'core', origin: 'Core', unsupported: 'À venir', insert: () => ({ refused: `« ${widget.label} » n’est pas encore disponible.` }) });
      continue;
    }
    out.push(blockEntry(registry, component.name, { label: widget.label, word: widget.word, icon: widget.icon, meta: widget.meta, section: widget.group }));
  }
  // Les autres blocs du registre (composites d'un Pack ou du client) rejoignent « Affichage ».
  const named = new Set(WIDGETS.flatMap((widget) => widget.blocks ?? []));
  for (const component of registry.components) if (!named.has(component.name) && blockOf(component) !== undefined) out.push(blockEntry(registry, component.name, { section: 'Affichage' }));
  return out;
}

function blockEntry(registry: EditorRegistry, name: string, look: { label?: string; word?: string; icon?: string; meta?: string; section: string }): LibraryEntry<Template> {
  const component = componentByName(registry, name)!;
  const block = blockOf(component)!;
  const layer = (component.origin as Layer) ?? 'core';
  return {
    id: `block:${component.name}`,
    label: look.label ?? fr(component.manifest.component.label, 'Bloc'),
    ...(look.word === undefined ? {} : { word: look.word }),
    group: 'Widgets',
    section: look.section,
    icon: look.icon ?? 'layout',
    ...(look.meta === undefined ? {} : { meta: look.meta }),
    layer,
    origin: layer === 'core' ? 'Core' : layer === 'pack' ? 'Pack' : 'Client',
    ...(flutterSupported(component) ? {} : { unsupported: 'Web seulement' }),
    insert: (template, target) => {
      const spot = blockSpot(template, registry, target, block.category);
      if (spot === null) return { refused: 'Choisissez d’abord un modèle de page.' };
      const inserted = insertBlock(template, registry, component.name, spot.region, { ...(spot.index === undefined ? {} : { index: spot.index }), ...(spot.page_ids === undefined ? {} : { page_ids: spot.page_ids }) });
      const id = inserted.id;
      // Bouton d'action et puce de compteur : réglages par défaut valides (ADR-146 addendum (20)).
      const next = withDisplayDefaults(inserted.template, component.name, id);
      const refused = refusalOf(registry, template, next);
      return refused === null ? { document: next, id: `block:${id}` } : { refused };
    },
  };
}

// ---- Parties (cartes).

function partEntries(registry: EditorRegistry, template: Template): LibraryEntry<Template>[] {
  const out: LibraryEntry<Template>[] = SECTION_KINDS.map((kind) => ({
    id: `section:${kind.id}`,
    label: kind.id === 'series' ? 'Liste de lignes' : 'Partie',
    word: kind.id === 'series' ? 'Liste' : 'Partie',
    group: 'Parties',
    icon: kind.id === 'series' ? 'rows' : 'layers',
    meta: kind.id === 'series' ? 'Une ligne par élément saisi' : 'Un groupe de champs',
    layer: 'core' as const,
    origin: 'Core',
    card: true,
    insert: (document: Template, target: string | null) => insertSection(document, registry, kind.create(document) as Section, target),
  }));
  const strip = currentLayout(registry, template)?.body.regions.find((region) => (region as { role?: string }).role === 'strip');
  if (strip !== undefined) {
    out.push({
      id: 'section:strip',
      label: 'Bande de champs',
      word: 'Bande',
      group: 'Parties',
      icon: 'segmented',
      meta: 'Champs compacts sous le bandeau',
      layer: 'core',
      origin: 'Core',
      card: true,
      insert: (document, target) => insertSection(document, registry, SECTION_KINDS[0]!.create(document) as Section, target ?? `region:${strip.region_id}`),
    });
  }
  return out;
}

// ---- Champs du modèle, groupés par objet.

const humanize = (code: string): string => {
  const words = code.replace(/_/g, ' ').trim();
  return words === '' ? code : words.charAt(0).toLocaleUpperCase('fr') + words.slice(1);
};

/** Entrée de champ créée depuis un champ du modèle : même identifiant si libre, widget par défaut de son type. */
export function modelFieldEntry(template: Template, object: ModelObject, source: ModelField): Entry {
  const fieldId = freeFieldId(template, source.field_id);
  const field: Record<string, unknown> = { field_id: fieldId, label: { ...source.label }, type: source.type };
  if (source.unit !== undefined) field['unit'] = source.unit;
  if (source.reference_type !== undefined) field['reference_type'] = source.reference_type;
  const presentation: Record<string, unknown> = {};
  if (source.type === 'enumeration') {
    const values = source.enum_values ?? [];
    field['validation'] = { enum_values: values, enum_labels: Object.fromEntries(values.map((value) => [value, { fr: humanize(value) }])) };
    presentation['widget'] = values.length <= 4 ? 'segmented' : 'radio_tiles';
    presentation['option_source'] = { object_type: object.object_type, field_id: source.field_id };
  } else if (source.type === 'reference') presentation['widget'] = 'selector';
  else if (source.type === 'boolean') presentation['widget'] = 'toggle';
  else if (source.type === 'date') presentation['widget'] = 'date';
  else if (source.type === 'datetime') presentation['widget'] = 'datetime';
  else if (['number', 'decimal', 'quantity'].includes(source.type)) presentation['widget'] = 'keypad';
  return { field: field as unknown as Entry['field'], ...(Object.keys(presentation).length === 0 ? {} : { presentation: presentation as Entry['presentation'] }) } as Entry;
}

const MODEL_TYPES = new Set(['string', 'rich_text', 'number', 'decimal', 'quantity', 'money', 'boolean', 'date', 'datetime', 'enumeration', 'reference']);
const TYPE_ICON: Readonly<Record<string, string>> = { string: 'text', rich_text: 'text-long', number: 'hash', decimal: 'hash', quantity: 'scale', money: 'euro', boolean: 'toggle', date: 'calendar', datetime: 'clock', enumeration: 'tiles', reference: 'link' };
const TYPE_WORD: Readonly<Record<string, string>> = { string: 'Texte', rich_text: 'Texte long', number: 'Nombre', decimal: 'Nombre', quantity: 'Mesure', money: 'Montant', boolean: 'Oui / Non', date: 'Date', datetime: 'Date et heure', enumeration: 'Choix', reference: 'Objet' };

/** Objets du modèle que le formulaire cite (référence, source d'options, préréglage), en tête. */
function citedObjects(template: Template): Set<string> {
  const cited = new Set<string>();
  for (const section of template.sections) {
    for (const entry of [...section.fields, ...(section.repeat?.children?.fields ?? [])]) {
      if (entry.field.reference_type !== undefined) cited.add(entry.field.reference_type);
      const source = (entry.presentation as { option_source?: { object_type: string } } | undefined)?.option_source;
      if (source !== undefined) cited.add(source.object_type);
    }
  }
  return cited;
}

function modelEntries(catalog: StudioCatalog, template: Template): LibraryEntry<Template>[] {
  const used = usedFieldIds(template);
  const cited = citedObjects(template);
  const objects = [...catalog.model].sort((a, b) => Number(cited.has(b.object_type)) - Number(cited.has(a.object_type)) || fr(a.label, a.object_type).localeCompare(fr(b.label, b.object_type), 'fr'));
  const out: LibraryEntry<Template>[] = [];
  for (const object of objects) {
    for (const source of object.fields) {
      if (!MODEL_TYPES.has(source.type)) continue;
      const placed = used.has(source.field_id);
      out.push({
        id: `field:model:${object.object_type}.${source.field_id}`,
        label: text(source.label, 'fr', humanize(source.field_id)),
        group: 'Modèle',
        section: text(object.label, 'fr', object.object_type),
        icon: TYPE_ICON[source.type] ?? 'text',
        // Les noms du modèle sont longs (« Poids brut », « Date de tir ») : une ligne, le nom entier.
        row: true,
        meta: TYPE_WORD[source.type] ?? 'Champ',
        layer: source.origin,
        origin: layerLabel(catalog, source.origin, source.lineage),
        ...(placed ? { note: 'déjà sur la page' } : {}),
        insert: (document, target) => insertField(document, modelFieldEntry(document, object, source), target),
      });
    }
  }
  return out;
}

// ---- Préréglages.

/** Entrée de champ recopiée d'un préréglage, qui le cite à sa version exacte (contrat 1.10.0, `preset`). */
export function presetEntry(template: Template, row: PresetRow): Entry {
  const body = row.body;
  const field = { ...(structuredClone(body.field) as unknown as Record<string, unknown>) };
  field['field_id'] = freeFieldId(template, typeof field['field_id'] === 'string' ? (field['field_id'] as string) : undefined);
  const entry = structuredClone((body.entry ?? {}) as Record<string, unknown>);
  const presentation = { ...((entry['presentation'] ?? {}) as Record<string, unknown>) };
  if (presentation['widget'] === undefined) presentation['widget'] = body.widget;
  return { ...entry, field, presentation, preset: { ref: `${row.artifact_id}@${row.artifact_version}` } } as unknown as Entry;
}

const WIDGET_WORD: Readonly<Record<string, string>> = {
  keypad: 'Nombre',
  slider: 'Curseur',
  radio_tiles: 'Choix en tuiles',
  toggle: 'Oui / Non',
  signature: 'Signature',
  scan_code: 'Code scanné',
  scan_doc: 'Document scanné',
  photo: 'Photo',
  selector: 'Objet à choisir',
  person: 'Personne',
  stepper: 'Compteur',
  date: 'Date',
  datetime: 'Date et heure',
  time: 'Heure',
  stars: 'Étoiles',
  lot: 'Lot',
  segmented: 'Choix segmenté',
};

export const widgetWord = (widget: string | undefined): string => (widget === undefined ? 'Champ' : (WIDGET_WORD[widget] ?? 'Champ'));

function presetEntries(catalog: StudioCatalog): LibraryEntry<Template>[] {
  return catalog.presets.map((row) => {
    const origin = layerLabel(catalog, row.origin, row.owner_ref);
    const source = row.body.binding === undefined ? '' : ` · ${catalog.model.find((object) => object.object_type === row.body.binding!.object_type)?.label['fr'] ?? 'objet du modèle'}`;
    return {
      id: `field:preset:${row.artifact_id}`,
      label: text(row.body.label, 'fr', 'Préréglage'),
      group: 'Préréglages',
      section: row.origin === 'core' ? 'Communs' : origin,
      icon: widgetById(presetWidgetId(row.body.widget))?.icon ?? 'copy',
      row: true,
      meta: `${widgetWord(row.body.widget)}${source}${row.forms > 0 ? ` · dans ${row.forms} formulaire${row.forms > 1 ? 's' : ''}` : ''}`,
      layer: row.origin,
      origin,
      insert: (template, target) => insertField(template, presetEntry(template, row), target),
    };
  });
}

// ---- À placer : parties qu'aucune page déclarée ne place.

/** Parties qu'aucune page ne cite (elles s'affichent en fin de dernière page tant qu'on ne les place pas). */
export function unplacedSections(template: Template): Section[] {
  const pages = declaredPages(template);
  if (pages.length === 0) return [];
  const cited = new Set(pages.flatMap((page) => page.sections));
  return template.sections.filter((section) => !cited.has(section.section_id));
}

/** Page visée par un point d'insertion (partie, champ, bloc, zone d'une page), sinon la première. */
function pageOfTarget(template: Template, target: string | null): string | undefined {
  const pages = formPages(template);
  if (target === null) return pages[0]?.page_id;
  if (target.startsWith('region:')) return regionTarget(target).page ?? pages[0]?.page_id;
  const section = /(?:^|:)section:([a-z][a-z0-9_]*)$/.exec(target)?.[1] ?? template.sections[Number(/field:\/sections\/(\d+)/.exec(target)?.[1] ?? -1)]?.section_id;
  if (section !== undefined) return pages.find((page) => page.sections.includes(section))?.page_id ?? pages[0]?.page_id;
  const block = /block:([a-z][a-z0-9_]*)$/.exec(target)?.[1];
  if (block !== undefined) return blocksOf(template).find((candidate) => candidate.block_id === block)?.page_ids?.[0] ?? pages[0]?.page_id;
  return pages[0]?.page_id;
}

function unplacedEntries(template: Template): LibraryEntry<Template>[] {
  return unplacedSections(template).map((section) => ({
    id: `section:unplaced:${section.section_id}`,
    label: text(section.label, 'fr', 'Partie'),
    group: 'Parties',
    pinned: true,
    icon: 'layers',
    meta: `${section.fields.length} champ${section.fields.length > 1 ? 's' : ''}`,
    insert: (document, target) => {
      const page = pageOfTarget(document, target);
      if (page === undefined) return { refused: 'Ajoutez d’abord une page.' };
      return { document: moveSectionToPage(document, section.section_id, page), id: `section:${section.section_id}` };
    },
  }));
}

/** Toutes les entrées de la bibliothèque v2 pour un gabarit. */
export function libraryV2(registry: EditorRegistry, catalog: StudioCatalog, template: Template): LibraryEntry<Template>[] {
  return [...unplacedEntries(template), ...widgetEntries(registry), ...partEntries(registry, template), ...modelEntries(catalog, template), ...presetEntries(catalog)];
}

/** Pointeur d'une entrée de champ → entrée (aide aux tests et à l'inspecteur). */
export const entryAt = (template: Template, pointer: string): Entry | undefined => getAt(template, pointer) as Entry | undefined;
